import { Link, useLocation } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/screener", label: "Screener" },
  { to: "/who-buys", label: "Customer Map" },
];
const DIMENSIONS = [
  { to: "/screener?dimension=customer", label: "Customer" },
  { to: "/screener?dimension=geographic", label: "Geographic" },
  { to: "/screener?dimension=supplier", label: "Supplier" },
  { to: "/screener?dimension=product", label: "Product" },
  { to: "/screener?dimension=credit", label: "Credit" },
];

export default function Navbar() {
  const loc = useLocation();
  const { user, login, logout } = useAuth();
  const dimActive = loc.pathname === "/screener" && /dimension=(customer|geographic|supplier|product|credit)/.test(loc.search);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
          <span className="font-heading text-lg font-bold tracking-tight text-neutral-900">
            Concentration of Risk
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors hover:text-neutral-900 ${
                loc.pathname + loc.search === n.to ? "text-neutral-900 font-medium" : "text-neutral-500"
              }`}
            >
              {n.label}
            </Link>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-concentrations"
                className={`flex items-center gap-1 text-sm tracking-wide transition-colors hover:text-neutral-900 focus:outline-none ${
                  dimActive ? "text-neutral-900 font-medium" : "text-neutral-500"
                }`}
              >
                Concentrations
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              {DIMENSIONS.map((d) => (
                <DropdownMenuItem key={d.label} asChild>
                  <Link
                    to={d.to}
                    data-testid={`nav-dim-${d.label.toLowerCase()}`}
                    className="w-full cursor-pointer"
                  >
                    {d.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-[#e8e6df] py-1 pl-1 pr-2.5 transition-colors hover:border-neutral-400">
                  {user.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-7 w-7 rounded-full" />
                  ) : (
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-[#1a1a18] text-xs font-semibold text-white">
                      {(user.name || user.email || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="truncate text-sm font-medium text-neutral-900" data-testid="nav-user-name">{user.name}</div>
                  <div className="truncate text-xs text-neutral-500">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout" onClick={logout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button
                data-testid="nav-login-btn"
                onClick={() => login("navbar_login")}
                className="text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900"
              >
                Log in
              </button>
              <button
                data-testid="nav-signup-btn"
                onClick={() => login("navbar_signup")}
                className="rounded-full bg-[#E24B4A] px-4 py-2 text-sm font-medium text-white transition-transform active:scale-95 hover:opacity-90"
              >
                Sign up free
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
